import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'
import { Action, Location } from 'history'
import { SideSheetsHistoryContext } from '../context/SideSheetsHistoryContext'

interface SideSheet {
  key: string
  location: Location
  animation: 'leaving' | 'entering'
  layer: number
  index: number
  onLeave: () => void
}

export const SideSheetsElementsContext = createContext<SideSheet[]>([])

export const SideSheetsElementsProvider = ({
  children
}: PropsWithChildren<unknown>) => {
  const history = useContext(SideSheetsHistoryContext)!

  const [sideSheets, setSideSheets] = useState<
    Omit<SideSheet, 'layer' | 'index' | 'onLeave'>[]
  >(
    history.entries.map(location => ({
      key: location.key,
      location,
      animation: 'entering'
    }))
  )

  useEffect(() => {
    return history.listen(({ action, location }) => {
      setSideSheets(prevSideSheets => {
        if (action === Action.Pop) {
          const leavingSideSheetIndex = prevSideSheets.findIndex(
            sideSheet => sideSheet.key === location.key
          )

          return prevSideSheets.map((sideSheet, index) => {
            if (leavingSideSheetIndex < index) {
              return {
                ...sideSheet,
                animation: 'leaving' as const
              }
            }
            return sideSheet
          })
        }
        if (action === Action.Push) {
          const newEntry = {
            key: location.key,
            location,
            animation: 'entering' as const
          }
          return [...prevSideSheets, newEntry]
        }
        if (action === Action.Replace) {
          const newEntry = {
            key: location.key,
            location,
            animation: 'entering' as const
          }

          const [lastSideSheet, ...restSideSheets] = prevSideSheets.reverse()

          if (lastSideSheet) {
            return [
              ...restSideSheets,
              newEntry,
              {
                ...lastSideSheet,
                animation: 'leaving' as const
              }
            ]
          }
          return [...prevSideSheets, newEntry]
        }

        return prevSideSheets
      })
    })
  }, [history])

  const onLeave = useCallback((location: Location) => {
    setSideSheets(sideSheets => sideSheets.filter(e => e.key !== location.key))
  }, [])

  let length = sideSheets.length
  if (sideSheets.find(s => s.animation === 'leaving')) {
    length = length - 1
  }

  const finalSideSheets: SideSheet[] = sideSheets.map((sideSheet, index) => ({
    ...sideSheet,
    layer: length - index,
    index: index,
    location: sideSheet.location,
    onLeave: () => onLeave(sideSheet.location)
  }))

  return (
    <SideSheetsElementsContext.Provider value={finalSideSheets}>
      {children}
    </SideSheetsElementsContext.Provider>
  )
}
